/**
 * Authorization entity DTOs and constants
 * Extracted from src/services/patientmr/authorizations.service.ts - API contract types only
 */

import { AuditFields } from '../common/base.dto';
import { PA_CATEGORY_ENTRIES } from '../../constants/disciplineRegistry';
import { paCategoryEntries } from '../../constants/disciplines';
import { DisciplineApi } from '../common/discipline.dto';

/** Phase 20C: a PT / OT / ST PA links its F00139 (TX_THERAPIES_PA) */
export type PALinkedFormType = 'CCP_PA_REQUEST' | 'CCP_PA_REAUTH' | 'TX_THERAPIES_PA';

/** Phase 20C (TMPPM PT/OT/ST §5.2.4–5.2.5): acute up to 60 days (modifier AT), chronic up to 180 */
export const THERAPY_ACUITIES = ['ACUTE', 'CHRONIC'] as const;
export type TherapyAcuity = typeof THERAPY_ACUITIES[number];
export const THERAPY_PERIOD_MAX_DAYS: Readonly<Record<TherapyAcuity, number>> = { ACUTE: 60, CHRONIC: 180 };

/** Phase 20D: the attendant programme an MCO authorized (constants ATTENDANT_PROGRAM_ENTRIES) */
export type AttendantProgram = 'PCS' | 'CFC_PAS' | 'CFC_HAB';

/**
 * @deprecated Use PAStatus instead. This enum is kept for backwards compatibility only.
 * PA_STATUSES / PAStatus is the canonical status set for the PA module.
 * Migration: replace all references to AuthorizationStatus with PAStatus.
 * This export will be removed once all consumers are migrated.
 */
export enum AuthorizationStatus {
  PENDING = 'pending',
  ACTIVE = 'active',
  EXPIRED = 'expired',
  CANCELLED = 'cancelled'
}

// ═══════════════════════════════════════════════════════════════════════════
// PA Module Enums & Types
// ═══════════════════════════════════════════════════════════════════════════

export enum PAStatus {
  DRAFT = 'DRAFT',
  SUBMITTED = 'SUBMITTED',
  PENDED = 'PENDED',
  APPROVED = 'APPROVED',
  DENIED = 'DENIED',
  EXPIRED = 'EXPIRED',
  EXHAUSTED = 'EXHAUSTED'
}

export enum PAPayerType {
  MEDICAID = 'medicaid',
  PRIVATE = 'private'
}

export enum MedicaidProgram {
  STAR_KIDS = 'star_kids',
  STAR_PLUS = 'star_plus',
  FFS = 'ffs'
}

/**
 * PA service categories (Phase 20A): from the discipline registry, keyed in upper case
 * (`ServiceCategory.PDN === 'pdn'`, `ServiceCategory.SN_VISIT === 'sn_visit'`).
 */
type ActivePaCategory = Extract<(typeof PA_CATEGORY_ENTRIES)[number], { status: 'active' }>['code'];
export const ServiceCategory = Object.freeze(Object.fromEntries(
  paCategoryEntries().map(c => [c.code.toUpperCase(), c.code])
)) as { readonly [K in ActivePaCategory as Uppercase<K>]: K };
// eslint-disable-next-line @typescript-eslint/no-redeclare -- intentional type/value pair (enum replacement)
export type ServiceCategory = ActivePaCategory;

export enum PARequestType {
  INITIAL = 'initial',
  RECERTIFICATION = 'recertification',
  REVISION = 'revision'
}

export enum PAUnitType {
  UNITS = 'units',
  VISITS = 'visits',
  HOURS = 'hours'
}

export enum PAServiceType {
  RN_ONLY = 'RN only',
  LVN_ONLY = 'LVN only',
  RN_AND_LVN = 'RN & LVN'
}

export interface PAApprovalRequest {
  authNumber: string;
  approvedStartDate: string;
  approvedEndDate: string;
  approvedQuantity: number;
  approvedPerWeek: number;
  serviceType?: PAServiceType | null;
  /** Phase 17 (REQ-CLM-06): PDN approved with modifier UA (invasive ventilation / trach) */
  uaApproved?: boolean;
}

export interface PACreateRequest {
  payerType: PAPayerType;
  medicaidProgram?: MedicaidProgram | null;
  payerName: string;
  serviceCategory: ServiceCategory;
  requestType: PARequestType;
  unitType: PAUnitType;
  minutesPerUnit?: number | null;
  requestedStartDate: string;
  requestedEndDate: string;
  revisionReason?: string | null;
  // Legacy fields needed by backend
  discipline?: string;
  startDate?: string;
  expirationDate?: string;
  authorizedVisits?: number;
  authNumber?: string;
  notes?: string;
  linkedFormId?: string | null;
  linkedFormType?: PALinkedFormType | null;
  /** F00120 (NURSING_ADDENDUM) form linked to this PA (Phase 8 Q4) */
  linkedFlowSheetFormId?: string | null;
  /** Phase 20C: therapy PAs */
  therapyAcuity?: TherapyAcuity | null;
  /** Phase 20D: attendant PAs */
  attendantProgram?: AttendantProgram | null;
}

/**
 * Payer types for insurance
 */
export enum PayerType {
  MEDICARE = 'Medicare',
  MEDICAID = 'Medicaid',
  COMMERCIAL = 'Commercial',
  SELF_PAY = 'Self Pay',
  WORKERS_COMP = 'Workers Comp'
}

/**
 * Place of service codes
 */
export enum PlaceOfService {
  HOME = 'Home (12)',
  ASSISTED_LIVING = 'Assisted Living (13)',
  SKILLED_NURSING = 'Skilled Nursing Facility (31)',
  OFFICE = 'Office (11)',
  HOSPITAL_OUTPATIENT = 'Hospital Outpatient (22)'
}

/**
 * Quantity Type enum for approval quantity
 */
export enum QuantityType {
  VISITS = 'visits',
  UNITS_15MIN = '15min_units'
}

/**
 * Common medical specialties
 */
export enum MedicalSpecialty {
  FAMILY_MEDICINE = 'Family Medicine',
  INTERNAL_MEDICINE = 'Internal Medicine',
  CARDIOLOGY = 'Cardiology',
  ORTHOPEDICS = 'Orthopedics',
  NEUROLOGY = 'Neurology',
  PSYCHIATRY = 'Psychiatry',
  REHABILITATION = 'Rehabilitation Medicine',
  GERIATRICS = 'Geriatrics',
  OTHER = 'Other'
}

/**
 * UI-facing Authorization discipline enum
 */
// Phase 20A: registry disciplines (same values the backend accepts)
export const AuthorizationDiscipline = DisciplineApi;
// eslint-disable-next-line @typescript-eslint/no-redeclare -- intentional type/value pair (enum replacement)
export type AuthorizationDiscipline = DisciplineApi;

/**
 * API-facing Authorization discipline enum (matches backend AUTHORIZATION_DISCIPLINES: RN/PT/OT/ST/MSW/HHA)
 */
// Phase 20A: registry disciplines (same values the backend accepts)
export const ApiAuthorizationDiscipline = DisciplineApi;
// eslint-disable-next-line @typescript-eslint/no-redeclare -- intentional type/value pair (enum replacement)
export type ApiAuthorizationDiscipline = DisciplineApi;

/**
 * Authorization document attachment
 */
export interface AuthorizationDocument {
  documentId?: string;
  documentName?: string;
  documentData?: string;
  contentType?: string;
  s3Key?: string;
  s3Url?: string;
  signedUrl?: string;
  uploadedAt?: string;
  uploadedBy?: string;
  id?: number;
  name?: string;
  type?: string;
  uploadDate?: string;
  url?: string;
}

/**
 * Authorization entity as received from the backend API
 */
export interface Authorization extends Partial<AuditFields> {
  id: number | string;
  authorizationId?: string;
  paNumber?: string; // Human-readable PA number (e.g. "PA202600000042"). Absent on legacy records.
  discipline: AuthorizationDiscipline;
  startDate: string;
  expirationDate: string;
  documents: (AuthorizationDocument | string)[];
  status: AuthorizationStatus | PAStatus | string;
  authNumber: string;
  visits: number;
  notes: string;
  authorizationType?: string;
  usedVisits?: number;
  remainingVisits?: number;
  insuranceProvider?: string | null;
  authorizedVisits?: number;
  quantityType?: QuantityType;
  timePerUnit?: string;
  hcpcsCode?: string;
  pocCertificationId?: string;
  pocCertificationStatus?: 'DRAFT' | 'PENDING_SIGNATURE' | 'SIGNED' | 'REJECTED' | 'EXPIRED';
  unitsUsed?: number;
  unitsRemaining?: number;
  lastClaimDate?: string;
  payorName?: string;
  payorPhone?: string;
  caseManagerName?: string;
  caseManagerPhone?: string;
  denialReason?: string;
  appealDate?: string;
  appealOutcome?: string;
  // ── PA Module Fields ──
  payerType?: PAPayerType | null;
  medicaidProgram?: MedicaidProgram | null;
  payerName?: string | null;
  serviceCategory?: ServiceCategory | null;
  requestType?: PARequestType | null;
  unitType?: PAUnitType | null;
  minutesPerUnit?: number | null;
  requestedStartDate?: string | null;
  requestedEndDate?: string | null;
  revisionReason?: string | null;
  approvedStartDate?: string | null;
  approvedEndDate?: string | null;
  approvedQuantity?: number | null;
  approvedPerWeek?: number | null;
  serviceType?: PAServiceType | null;
  /** Phase 17 (REQ-CLM-06): claims use the T1000 UA code row only when this is true */
  uaApproved?: boolean;
  /** Phase 20C: therapy PAs (drives the period cap and modifier AT) */
  therapyAcuity?: TherapyAcuity | null;
  /** Phase 20D: attendant PAs (sets the T1019 modifier) */
  attendantProgram?: AttendantProgram | null;
  usedQuantity?: number;
  documentCount?: number;
  linkedFormId?: string | null;
  linkedFormType?: PALinkedFormType | null;
  expirationAlertLevel?: 'green' | 'yellow' | 'orange' | 'red' | 'expired' | 'none';
  expirationDaysRemaining?: number | null;
  expirationIsExpired?: boolean;
  /** F00120 (NURSING_ADDENDUM) form linked to this PA (Phase 8 Q4) */
  linkedFlowSheetFormId?: string | null;
  /** PA-11 deadline helpers; present only when the server computed them */
  deadlines?: PADeadlines | null;
  /** Response-only: approve may return a notice (e.g. approved past its end date → EXPIRED, PA-03) */
  notice?: string | null;
}

/** PA-11: initial PA due date and recert window (YYYY-MM-DD agency dates). */
export interface PADeadlines {
  initialDueDate?: string | null;
  recertWindow?: { start: string; end: string } | null;
}

/**
 * Authorization request for create/update
 */
export interface AuthorizationRequest {
  discipline: AuthorizationDiscipline | string;
  startDate: string;
  expirationDate: string;
  authNumber: string;
  visits?: number;
  authorizedVisits?: number;
  notes?: string;
  authorizationType?: string;
  insuranceProvider?: string;
  quantityType?: QuantityType;
  hcpcsCode?: string;
  pocCertificationId?: string;
  documents?: AuthorizationDocument[];
  // PA Module fields (optional — present only on PA-type authorizations)
  payerType?: string | null;
  medicaidProgram?: string | null;
  payerName?: string | null;
  serviceCategory?: string | null;
  requestType?: string | null;
  unitType?: string | null;
  minutesPerUnit?: number | null;
  requestedStartDate?: string | null;
  requestedEndDate?: string | null;
  revisionReason?: string | null;
  linkedFormId?: string | null;
  linkedFormType?: PALinkedFormType | null;
  linkedFlowSheetFormId?: string | null;
  therapyAcuity?: TherapyAcuity | null;
  attendantProgram?: AttendantProgram | null;
}

export interface PADocument {
  id: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  s3Key: string;
  uploadedAt: string;
  uploadedBy: string;
  signedUrl?: string;
}

export interface PALinkedVisit {
  visitId: string;
  date: string;
  clinician: string;
  type: string;
  durationMinutes: number;
  unitsConsumed: number;
}

export interface PAUsageResponse {
  authorizationId: string;
  unitType: PAUnitType;
  minutesPerUnit: number | null;
  approvedQuantity: number;
  usedQuantity: number;
  remaining: number;
  percentUsed: number;
  visitCount: number;
  linkedVisits: PALinkedVisit[];
}

// ── Phase 8: documents (pre-signed), submit warnings, packet, MCO list ──────

/** Body of POST …/authorization/{id}/documents (confirms a pre-signed upload). */
export interface PADocumentConfirmRequest {
  key: string;
  documentName: string;
  documentType: string;
}

/** Non-blocking submit warning (PA-11/12/13): no documents, < 7 days lead, under-21, ... */
export interface PASubmitWarning {
  code: string;
  message: string;
}

export interface PASubmitPreflight {
  warnings: PASubmitWarning[];
}

export interface PASubmitOptions {
  dryRun?: boolean;
  acknowledgeWarnings?: boolean;
}

/** One gap from the 422 FLOWSHEET_INCOMPLETE submit gate. */
export interface PAFlowSheetError {
  day: string;
  time?: string;
  code: string;
}

/** POST …/authorization/{id}/packet response. */
export interface PAPacketResponse {
  url: string;
  version: number;
  forms: string[];
}

/** MCO choice from systemConfig.payers.medicaidMcos (programs filter is optional). */
export interface PAMcoOption {
  code: string;
  label: string;
  programs?: string[];
  legacy?: boolean;
}

/**
 * Default HCPCS/CPT code and unit per discipline for a new prior authorization request (PA form
 * prefill only; the user can change it). Claim lines never use this: they come from the tenant
 * BILLING_CONFIG (Billing → Codes & rates).
 */
export const HCPCS_BY_DISCIPLINE: Record<string, { code: string; description: string; timePerUnit: string }> = {
  'RN': { code: 'T1000', description: 'Private Duty Nursing (PDN)', timePerUnit: '15 minutes' },
  'PT': { code: '97110', description: 'Therapeutic Exercises', timePerUnit: '15 minutes' },
  'OT': { code: '97530', description: 'Therapeutic Activities', timePerUnit: '15 minutes' },
  'ST': { code: '92507', description: 'Speech/Language Treatment', timePerUnit: 'Per session' },
  'MSW': { code: '99211', description: 'Medical Social Work', timePerUnit: 'Per session' },
  'HHA': { code: 'G0156', description: 'Home Health Aide', timePerUnit: 'Per visit' }
};


